/**
 * One tile in the multi-chart workspace.
 *
 * Holds its own data fetch, its own symbol/timeframe pickers, its own
 * indicator overlays and its own Pine script. There is no shared state
 * between tiles -- changing one does not disturb the others, which is the
 * whole point of this view.
 */
import { useEffect, useRef, useState } from "react";

import type { DrawingKind, DrawingManager } from "lightweight-charts-drawing";

import { Chart } from "../components/Chart";
import { Drawings } from "../components/Drawings";
import { PineDrawings } from "../components/PineDrawings";
import { useAsync } from "../hooks/useApi";
import { api } from "../lib/api";
import { useCrosshairPublisher, useSyncedCrosshair, useWindowId } from "./crosshair";
import { useTileRun, type TileCatalogue } from "./useTileRuns";
import type { Tile } from "./workspace";

const TIMEFRAMES = ["1m", "5m", "15m", "30m", "1h", "4h", "1d", "1w"] as const;

export interface ChartTileProps {
  tile: Tile;
  catalogue: TileCatalogue;
  onChange: (patch: Partial<Tile>) => void;
  onRemove: () => void;
  onPopOut: () => void;
  onDragStart: (ev: React.PointerEvent) => void;
  onResizeStart: (ev: React.PointerEvent) => void;
  dragging: boolean;
  /** Enabled when crosshair sync is on. Pointer moves on this tile broadcast
   *  their bar-index, and we subscribe to the same channel to draw a ghost
   *  crosshair when another tile is being read. */
  crosshairSync?: boolean;
}

export function ChartTile({
  tile, catalogue, onChange, onRemove, onPopOut,
  onDragStart, onResizeStart, dragging, crosshairSync,
}: ChartTileProps) {
  const bars = useAsync(
    (s) => api.bars(tile.symbol, tile.timeframe, 500, false, s),
    [tile.symbol, tile.timeframe],
  );
  const run = useTileRun(tile, catalogue);
  const inputRef = useRef<HTMLInputElement>(null);
  // One picker now covers both indicators and strategies (TradingView-style).
  // Was two toggles ("indicators"/"script"); users had to know which button
  // opened which library, which is exactly the "strategy feels like just
  // another indicator" problem.
  const [pickerOpen, setPickerOpen] = useState(false);

  // Cross-tile crosshair sync: publish our hovered time, consume everyone
  // else's. Both halves are no-ops when the workspace preference is off.
  const windowId = useWindowId();
  const publishCrosshair = useCrosshairPublisher(windowId, !!crosshairSync);
  const syncTime = useSyncedCrosshair(windowId, !!crosshairSync);

  // Tile-local drawing tool state. Each tile has its own armed tool so
  // dragging a trendline on one tile doesn't put another into draw mode.
  // Storage is scoped by tile id too (see <Drawings scope=... />), so two
  // tiles on the same symbol keep their own set of lines/boxes/notes.
  const [drawTool, setDrawTool] = useState<DrawingKind | null>(null);
  const drawMgrRef = useRef<DrawingManager | null>(null);

  // Auto-grow the tile when its chart needs more vertical room than the
  // user's current `h` provides. Each separate-pane indicator or pine pane
  // adds ~2 grid cells of required height on top of the 4-cell base for the
  // price pane itself. Shrink is never automatic -- the user may have
  // deliberately resized tight, and shrinking under them would be rude.
  const needed = 4 + run.panes.length * 2;
  useEffect(() => {
    if (tile.h < needed) onChange({ h: needed });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needed]);

  return (
    <div className={`mc-tile ${dragging ? "mc-tile-drag" : ""}`}>
      <div className="mc-tile-head" onPointerDown={onDragStart}>
        <input
          ref={inputRef}
          className="mc-sym"
          defaultValue={tile.symbol}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
          onBlur={(e) => {
            const v = e.currentTarget.value.trim().toUpperCase();
            if (v && v !== tile.symbol) onChange({ symbol: v });
          }}
          onPointerDown={(e) => e.stopPropagation()}
        />
        <select
          className="mc-tf"
          value={tile.timeframe}
          onChange={(e) => onChange({ timeframe: e.target.value })}
          onPointerDown={(e) => e.stopPropagation()}
          title="Timeframe"
        >
          {TIMEFRAMES.map((tf) => <option key={tf} value={tf}>{tf}</option>)}
        </select>

        {/* Unified "Add" library -- matches TradingView's single
            "Indicators, metrics, and strategies" dialog. The badge shows the
            total count of things attached to this tile (indicators + the
            strategy if one is loaded) so a glance tells you what the tile
            is running. */}
        {(() => {
          const activeCount = (tile.indicatorIds?.length ?? 0) + (tile.scriptId ? 1 : 0);
          return (
            <button
              className={`mc-icon mc-lib-btn ${activeCount > 0 ? "mc-icon-on" : ""}`}
              title="Add indicators or a strategy"
              onClick={(e) => { e.stopPropagation(); setPickerOpen(true); }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <span className="mc-lib-plus">＋</span>
              <span className="mc-lib-label">fx</span>
              {activeCount > 0 && <span className="mc-lib-count">{activeCount}</span>}
            </button>
          );
        })()}

        <span className="mc-flex" />

        {/* Minimal drawing toolbar: cursor, trendline, horizontal, text, erase.
            These five cover ~90% of what people draw on a chart. Any of the
            other 81 tools are available from the single-chart view, and
            drawings carry per-tile. */}
        {([
          ["cursor", null, "⇗"],
          ["trend-line", "trend-line", "／"],
          ["horizontal-line", "horizontal-line", "─"],
          ["text", "text", "T"],
        ] as const).map(([key, kind, glyph]) => (
          <button
            key={key}
            className={`mc-icon ${drawTool === kind ? "mc-icon-on" : ""}`}
            title={key === "cursor" ? "Cursor (no tool)" : `Draw ${key}`}
            onClick={(e) => {
              e.stopPropagation();
              setDrawTool(drawTool === kind ? null : (kind as DrawingKind | null));
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >{glyph}</button>
        ))}
        <button
          className="mc-icon"
          title="Erase every drawing on this tile"
          onClick={(e) => {
            e.stopPropagation();
            // confirm because this is destructive and silent to undo
            if (!window.confirm("Erase every drawing on this tile?")) return;
            drawMgrRef.current?.clear();
          }}
          onPointerDown={(e) => e.stopPropagation()}
        >🗑</button>

        <button
          className="mc-icon"
          title="Open in a new window"
          onClick={onPopOut}
          onPointerDown={(e) => e.stopPropagation()}
        >↗</button>
        <button
          className="mc-icon mc-close"
          title="Remove this chart"
          onClick={onRemove}
          onPointerDown={(e) => e.stopPropagation()}
        >×</button>
      </div>

      <div className="mc-tile-body">
        {bars.data && bars.data.bars.length > 0 ? (
          <Chart
            bars={bars.data.bars}
            timeframe={tile.timeframe}
            caption={`${tile.symbol} · ${tile.timeframe}`}
            viewKey={`${tile.id}:${tile.symbol}:${tile.timeframe}`}
            volumeMode={tile.volume}
            overlayGroups={run.overlayGroups}
            panes={run.panes}
            shapes={run.shapes}
            labels={run.labels}
            tables={run.tables}
            onCrosshair={publishCrosshair}
            syncCrosshairTime={syncTime}
            renderPriceOverlay={({ chart, series }) => (
              <>
                {(run.lines.length > 0 || run.boxes.length > 0
                  || run.polylines.length > 0 || run.linefills.length > 0) && (
                  <PineDrawings
                    chart={chart}
                    series={series}
                    barTimes={bars.data!.bars.map((b) => b.time)}
                    lines={run.lines}
                    boxes={run.boxes}
                    polylines={run.polylines}
                    linefills={run.linefills}
                  />
                )}
                <Drawings
                  chart={chart}
                  series={series}
                  bars={bars.data!.bars}
                  symbol={tile.symbol}
                  scope={tile.id}
                  tool={drawTool}
                  magnet={false}
                  stayInDrawingMode={false}
                  onToolChange={setDrawTool}
                  onManager={(m) => { drawMgrRef.current = m; }}
                />
              </>
            )}
          />
        ) : bars.error ? (
          <div className="mc-msg mc-err">{bars.error.message || "could not load bars"}</div>
        ) : (
          <div className="mc-msg mc-dim">loading {tile.symbol}…</div>
        )}

        {run.pineError && (
          <div className="mc-tile-pine-err" title={run.pineError}>
            Pine error: {run.pineError.slice(0, 60)}{run.pineError.length > 60 ? "…" : ""}
          </div>
        )}
      </div>

      {pickerOpen && (
        <PickerBackdrop onClose={() => setPickerOpen(false)}>
          <LibraryPicker
            tile={tile}
            catalogue={catalogue}
            onChange={onChange}
            onClose={() => setPickerOpen(false)}
          />
        </PickerBackdrop>
      )}

      <div
        className="mc-resize"
        onPointerDown={onResizeStart}
        title="Drag to resize"
      />
    </div>
  );
}

/** Clickable backdrop + Escape key handler for pickers.
 *
 *  A click outside the panel dismisses it; Escape does the same from the
 *  keyboard. Without this the picker had no obvious exit — the small ×
 *  button at the top is easy to miss, and if the user scrolls the panel
 *  the × scrolls out of view entirely.
 */
function PickerBackdrop({
  onClose, children,
}: {
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="mc-picker-backdrop"
      onPointerDown={(e) => {
        // Only close when the backdrop itself is clicked, not when a click
        // on an inner control bubbles up.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {children}
    </div>
  );
}

/** TradingView-style "Indicators, metrics, and strategies" dialog.
 *
 *  One picker for both indicators and strategies, matching the layout of the
 *  Chart → Indicators dialog on TradingView: left rail of sections, top
 *  filter chips (All / Indicators / Strategies), a search bar, and a table
 *  of items. Clicking an indicator attaches it to the tile with defaults;
 *  clicking a strategy sets it as the tile's Pine script. Both show an
 *  "Added" state and can be removed in-place.
 */
function LibraryPicker({
  tile, catalogue, onChange, onClose,
}: {
  tile: Tile;
  catalogue: TileCatalogue;
  onChange: (patch: Partial<Tile>) => void;
  onClose: () => void;
}) {
  type Section = "all" | "my_scripts" | "built_in";
  type Chip = "all" | "indicators" | "strategies";
  // Default to "All" so the first thing a user sees is every option available
  // (built-in indicators AND their saved scripts, both indicators and
  // strategies). TV does the same -- the Favorites tab opens by default
  // showing a mix of indicator kinds. Previously the picker opened on
  // "Built-in", so strategies -- which live in My scripts -- were invisible
  // until the user noticed the left-rail section labels.
  const [section, setSection] = useState<Section>("all");
  const [chip, setChip] = useState<Chip>("all");
  const [q, setQ] = useState("");
  const allInstances = readIndicatorInstances();

  // Items the user has already attached, for the top "On this tile" row.
  const currentInstances = (tile.indicatorIds ?? [])
    .map((id) => allInstances.find((i) => i.instance_id === id))
    .filter((i): i is NonNullable<typeof i> => !!i);
  const currentScript = catalogue.scripts.find((s) => s.id === tile.scriptId) ?? null;
  const anyAttached = currentInstances.length + (currentScript ? 1 : 0) > 0;

  // ── Row model ────────────────────────────────────────────────────────
  interface Row {
    key: string;
    name: string;
    kind: "indicator" | "strategy";
    source: "built_in" | "my_scripts";
    meta: string;              // category / "You" — the "author" cell on TV
    attached: boolean;
    onAdd: () => void;
    onRemove?: () => void;
  }

  const addIndicatorFromCatalogue = (indicatorId: string) => {
    const def = catalogue.indicators.get(indicatorId);
    if (!def) return;
    const taken = new Set(allInstances.map((i) => i.instance_id));
    let n = 1;
    let id = `${indicatorId}_${n}`;
    while (taken.has(id)) id = `${indicatorId}_${++n}`;
    const inst = { instance_id: id, indicator_id: indicatorId, params: { ...def.defaults } };
    writeIndicatorInstances([...allInstances, inst]);
    onChange({ indicatorIds: [...(tile.indicatorIds ?? []), id] });
  };

  const removeInstanceFromTile = (instanceId: string) => {
    onChange({ indicatorIds: (tile.indicatorIds ?? []).filter((i) => i !== instanceId) });
  };

  // Build the row list from the active section + chip + search.
  const rows: Row[] = [];
  const search = q.trim().toLowerCase();
  const matches = (name: string) => !search || name.toLowerCase().includes(search);

  // Section + chip together decide what's shown. "All" section includes
  // every source; the chip then filters by kind across those sources.
  const includeBuiltIn = section === "all" || section === "built_in";
  const includeMyScripts = section === "all" || section === "my_scripts";

  if (includeBuiltIn && chip !== "strategies") {
    // Built-in catalogue is indicators only -- no built-in strategies yet.
    for (const def of catalogue.list) {
      if (!matches(def.name)) continue;
      const attachedInstance = currentInstances.find((i) => i.indicator_id === def.id);
      rows.push({
        key: `b:${def.id}`,
        name: def.name,
        kind: "indicator",
        source: "built_in",
        meta: def.category || "built-in",
        attached: !!attachedInstance,
        onAdd: () => addIndicatorFromCatalogue(def.id),
        onRemove: attachedInstance
          ? () => removeInstanceFromTile(attachedInstance.instance_id)
          : undefined,
      });
    }
  }

  if (includeMyScripts) {
    for (const s of catalogue.scripts) {
      if (!matches(s.name)) continue;
      const k: "indicator" | "strategy" = s.kind === "strategy" ? "strategy" : "indicator";
      if (chip === "indicators" && k !== "indicator") continue;
      if (chip === "strategies" && k !== "strategy") continue;
      const attached = tile.scriptId === s.id;
      rows.push({
        key: `s:${s.id}`,
        name: s.name,
        kind: k,
        source: "my_scripts",
        meta: "You",
        attached,
        onAdd: () => onChange({ scriptId: attached ? null : s.id }),
        onRemove: attached ? () => onChange({ scriptId: null }) : undefined,
      });
    }
  }

  // Keep strategies first when "All" is selected -- in a mixed list they are
  // the richer thing and worth seeing without scrolling.
  if (chip === "all" && section === "all") {
    rows.sort((a, b) => {
      if (a.kind === b.kind) return 0;
      return a.kind === "strategy" ? -1 : 1;
    });
  }

  // Totals for the chip counts, matching TradingView's style.
  const totalIndicators = catalogue.list.length +
    catalogue.scripts.filter((s) => s.kind !== "strategy").length;
  const totalStrategies = catalogue.scripts.filter((s) => s.kind === "strategy").length;

  return (
    <div className="lib">
      <div className="lib-head">
        <span className="lib-title">Indicators, metrics, and strategies</span>
        <button className="mc-close" onClick={onClose} title="Close (Esc)">×</button>
      </div>

      <div className="lib-search">
        <input
          className="input"
          placeholder="Search by name"
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <div className="lib-body">
        <nav className="lib-rail">
          <div className="lib-rail-head">Browse</div>
          <button
            className={`lib-rail-item ${section === "all" ? "lib-rail-on" : ""}`}
            onClick={() => setSection("all")}
          >
            All
            <span className="lib-count">{catalogue.list.length + catalogue.scripts.length}</span>
          </button>
          <div className="lib-rail-head">Personal</div>
          <button
            className={`lib-rail-item ${section === "my_scripts" ? "lib-rail-on" : ""}`}
            onClick={() => setSection("my_scripts")}
          >
            My scripts
            <span className="lib-count">{catalogue.scripts.length}</span>
          </button>
          <div className="lib-rail-head">Built-in</div>
          <button
            className={`lib-rail-item ${section === "built_in" ? "lib-rail-on" : ""}`}
            onClick={() => setSection("built_in")}
          >
            Technicals
            <span className="lib-count">{catalogue.list.length}</span>
          </button>
        </nav>

        <div className="lib-main">
          <div className="lib-chips">
            {([
              ["all", "All", rows.length],
              ["indicators", "Indicators", totalIndicators],
              ["strategies", "Strategies", totalStrategies],
            ] as const).map(([key, label, count]) => (
              <button
                key={key}
                className={`lib-chip ${chip === key ? "lib-chip-on" : ""}`}
                onClick={() => setChip(key as Chip)}
              >
                {label}
                {chip === key && <span className="lib-chip-count">·{count}</span>}
              </button>
            ))}
          </div>

          {anyAttached && (
            <div className="lib-attached">
              <div className="lib-sub">On this tile</div>
              {currentInstances.map((inst) => (
                <div className="lib-attached-row" key={inst.instance_id}>
                  <span>{labelFor(inst)}</span>
                  <button className="lib-mini" onClick={() => removeInstanceFromTile(inst.instance_id)}>
                    Remove
                  </button>
                </div>
              ))}
              {currentScript && (
                <div className="lib-attached-row">
                  <span>
                    {currentScript.name}
                    <span className={`kind-badge kind-${currentScript.kind}`}
                          style={{ marginLeft: 8, fontSize: 10, padding: "1px 6px" }}>
                      {currentScript.kind}
                    </span>
                  </span>
                  <button className="lib-mini" onClick={() => onChange({ scriptId: null })}>
                    Remove
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="lib-table">
            <div className="lib-table-head">
              <span>Name</span>
              <span className="lib-col-src">Source</span>
              <span />
            </div>
            {rows.length === 0 ? (
              <div className="lib-empty">
                {section === "my_scripts" && catalogue.scripts.length === 0 ? (
                  <>You have no saved scripts yet. Save one from the <strong>Pine editor</strong> and it will show up here.</>
                ) : (
                  <>Nothing matches that search.</>
                )}
              </div>
            ) : (
              rows.map((r) => (
                <div className="lib-row" key={r.key}>
                  <div className="lib-row-name">
                    <span>{r.name}</span>
                    <span className={`kind-badge kind-${r.kind}`}
                          style={{ marginLeft: 8, fontSize: 10, padding: "1px 6px" }}>
                      {r.kind}
                    </span>
                  </div>
                  <div className="lib-col-src faint small">{r.meta}</div>
                  <div className="lib-row-action">
                    {r.attached ? (
                      <button className="lib-mini" onClick={() => r.onRemove?.()}>Remove</button>
                    ) : (
                      <button className="lib-add" onClick={() => r.onAdd()}>Add</button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function writeIndicatorInstances(instances: Array<{
  instance_id: string;
  indicator_id: string;
  params: Record<string, unknown>;
}>): void {
  try {
    localStorage.setItem("tt.indicators", JSON.stringify(instances));
    window.dispatchEvent(new StorageEvent("storage", { key: "tt.indicators" }));
  } catch {
    // quota/private mode: picks fall back to session-only until reload.
  }
}

function readIndicatorInstances(): Array<{
  instance_id: string;
  indicator_id: string;
  params: Record<string, unknown>;
}> {
  try {
    const raw = localStorage.getItem("tt.indicators");
    if (!raw) return [];
    const all = JSON.parse(raw);
    return Array.isArray(all) ? all : [];
  } catch {
    return [];
  }
}

function labelFor(inst: {
  indicator_id: string;
  params: Record<string, unknown>;
}): string {
  const parts: string[] = [inst.indicator_id.toUpperCase()];
  const n = inst.params["length"] ?? inst.params["period"];
  if (typeof n === "number") parts.push(String(n));
  return parts.join(" ");
}
